/**
 * Number, date and label formatting shared by every chart form.
 *
 * Two rules the whole chart layer follows:
 *  • Compact figures (12.9K, 4.2M) on marks, axes and tiles — where space is
 *    the constraint and the reader wants magnitude.
 *  • Exact, thousands-separated figures in tooltips and the table view — the
 *    two places a reader goes when they want the real number. Nothing is only
 *    ever shown rounded.
 */

export const isNumeric = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const UNITS: Array<{ limit: number; suffix: string }> = [
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'B' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'K' },
];

/** 1284 → "1.3K", 4_200_000 → "4.2M". For marks, axes and tile values. */
export const compact = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);

  for (const { limit, suffix } of UNITS) {
    if (abs >= limit) {
      const scaled = value / limit;
      // Keep one decimal only while it says something: 1.3M yes, 130.0M no.
      const digits = Math.abs(scaled) >= 100 ? 0 : 1;
      return `${trimZero(scaled.toFixed(digits))}${suffix}`;
    }
  }

  if (Number.isInteger(value)) return value.toLocaleString('en-GB');
  return trimZero(value.toFixed(Math.abs(value) < 1 ? 3 : 2));
};

const trimZero = (value: string): string =>
  value.includes('.') ? value.replace(/\.?0+$/, '') : value;

/** 1284 → "1,284". For tooltips and the table view. */
export const exact = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('en-GB', { maximumFractionDigits: 4 });
};

/** A percentage as it should read on a mark or in a legend. */
export const percent = (fraction: number, digits = 1): string =>
  `${(fraction * 100).toFixed(digits).replace(/\.0$/, '')}%`;

/** Signed, for deltas: +12.4% / −3.1%. Uses a real minus sign, not a hyphen. */
export const signed = (value: number, suffix = ''): string => {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${compact(Math.abs(value))}${suffix}`;
};

const ISO_DATE = /^\d{4}-\d{2}(-\d{2})?([T ]|$)/;

/**
 * "Mon, 06 Jul 2026 00:00:00 GMT" — what the chart-data API actually returns
 * for every date column (charts 6, 23, 30, 53, 54, 55, …), verified against
 * the live API on 2026-08-31.
 *
 * It is not ISO, so without this every time series was read as a column of
 * unrelated text: the axis showed the full 29-character string, the points
 * were not ordered as dates, and `suggestTemplate` never once chose a trend.
 */
const RFC_1123 = /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4}( \d{2}:\d{2}:\d{2})?( GMT| UTC)?$/;

const isDateLike = (value: string): boolean => ISO_DATE.test(value) || RFC_1123.test(value);

/** True when a column of labels looks like dates rather than free text. */
export const looksTemporal = (values: unknown[]): boolean => {
  const sample = values.filter((v) => v !== null && v !== undefined).slice(0, 20);
  if (sample.length < 2) return false;
  return sample.every((v) => typeof v === 'string' && isDateLike(v));
};

/**
 * A date-like value as a timestamp, or null when it is not a date.
 *
 * Exists so a caller can ORDER rows by a date column instead of trusting the
 * order the API returned. Both input formats denote an instant, so comparing
 * the numbers is sound; the month-only form is widened to the 1st, which is
 * what `shortDate` below already assumes.
 */
export const dateValue = (value: unknown): number | null => {
  if (typeof value !== 'string' || !isDateLike(value)) return null;
  const monthOnly = /^\d{4}-\d{2}$/.test(value);
  const time = new Date(monthOnly ? `${value}-01` : value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Shortens a date label for an axis: "2026-03-14" → "14 Mar", "2026-03" → "Mar 26".
 * Anything that is not a date is returned unchanged, so this is safe to apply
 * to every category label.
 */
export const shortDate = (value: string): string => {
  if (!isDateLike(value)) return value;

  const monthOnly = /^\d{4}-\d{2}$/.test(value);
  const date = new Date(monthOnly ? `${value}-01` : value);
  if (Number.isNaN(date.getTime())) return value;

  // Formatted in UTC, not the viewer's zone. Both input formats denote an
  // instant at UTC midnight, so rendering locally moves the label back a day
  // for anyone west of Greenwich: "Mon, 01 Dec 2025 00:00:00 GMT" reads as
  // "30 Nov" in New York, which at a month boundary reports the wrong month.
  return monthOnly
    ? date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' })
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

/** Any cell, formatted for display. Numbers compact, blanks as an em dash. */
export const cell = (value: unknown, style: 'compact' | 'exact' = 'compact'): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (isNumeric(value)) return style === 'exact' ? exact(value) : compact(value);
  return String(value);
};

/** "downloads_total" / "downloadsTotal" → "Downloads total". */
export const humanise = (column: string): string => {
  const spaced = column
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return column;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/** Trims a long category label for an axis without cropping mid-word twice. */
export const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;
